import { Avatar, Badge, Box, Button, Flex, Spinner, Text } from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { EmptyState } from '../../../components/EmptyState'
import { FilterBar, FilterField, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { LoadingBlock } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { SectionCard } from '../../../components/SectionCard'
import { SearchableSelect } from '../../../components/Select'
import { StatusBadge } from '../../../components/StatusBadge'
import { formatDateTime, useAppToast } from '../../../helpers'
import { EmployeeService } from '../../../services/Employee/EmployeeService'
import { IAdmissionCardRaw, IEmployeeFilters, TAdmissionStage } from '../../../services/Employee/types'
import { ADMISSION_CARD_BADGE } from '../Employees/employeeConstants'
import { EmployeeViewModal } from '../Employees/EmployeeViewModal'
import { useEmployeeOptions } from '../Employees/useEmployeeOptions'

// Each column loads this many cards at a time; "Carregar mais" fetches the next page.
const PAGE_SIZE = 8

/** The kanban columns: only "Pré Boarding" until the client defines the others (P13). */
const ADMISSION_STAGES: { stage: TAdmissionStage; label: string }[] = [{ stage: 'pre_boarding', label: 'Pré Boarding' }]

interface IColumnState {
  items: IAdmissionCardRaw[]
  total: number
  loadingMore: boolean
}

type BoardState = Record<TAdmissionStage, IColumnState>

const emptyBoard = (): BoardState =>
  ADMISSION_STAGES.reduce((acc, { stage }) => {
    acc[stage] = { items: [], total: 0, loadingMore: false }
    return acc
  }, {} as BoardState)

interface IFilters {
  search: string
  sectorId: string
  jobPositionId: number | null
}

const INITIAL_FILTERS: IFilters = { search: '', sectorId: '', jobPositionId: null }

function AdmissionCard({ card, onOpen }: { card: IAdmissionCardRaw; onOpen: (id: number) => void }) {
  return (
    <Box as="button" type="button" textAlign="left" w="100%" onClick={() => onOpen(card.id)}>
      <SectionCard variant="tile" w="100%" _hover={{ borderColor: 'magenta.300' }}>
        <Flex gap={3} align="center">
          <Avatar size="md" src={card.photo_url ?? undefined} name={card.name ?? undefined} />
          <Box minW={0} flex={1}>
            <Flex justify="space-between" align="flex-start" gap={2}>
              <Text fontWeight="bold" fontSize="sm" noOfLines={2}>
                {card.name}
              </Text>
              {ADMISSION_CARD_BADGE[card.status] && (
                <StatusBadge status={card.status} statuses={ADMISSION_CARD_BADGE} flexShrink={0} />
              )}
            </Flex>
            <Text variant="caption" noOfLines={1}>
              {card.position}
            </Text>
            <Text variant="caption">
              {card.job_requisition_number ? `RP nº ${card.job_requisition_number} · ` : ''}
              Enviado em {formatDateTime(card.submitted_at)}
            </Text>
          </Box>
        </Flex>
      </SectionCard>
    </Box>
  )
}

/**
 * "Admissão de Colaborador" (ABRA-320): the admissions sent through the public form, in kanban
 * columns. A card stays in its column when the person is approved or inactivated; clicking it
 * opens the same sheet as "Colaboradores". `?card=ID` opens it directly.
 */
export default function AdmissionsKanbanPage() {
  const service = useMemo(() => new EmployeeService(), [])
  const toast = useAppToast()
  const { roles, sectors, roleOptions } = useEmployeeOptions()
  const [searchParams, setSearchParams] = useSearchParams()

  const [board, setBoard] = useState<BoardState>(() => emptyBoard())
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [loadError, setLoadError] = useState(false)
  // Bumped by every board load: an answer from before it (another filter) is dropped.
  const boardVersion = useRef(0)
  const [filters, setFilters] = useState<IFilters>(INITIAL_FILTERS)
  const [openCardId, setOpenCardId] = useState<number | null>(null)

  const apiFilters: IEmployeeFilters = {
    search: filters.search,
    sector_id: filters.sectorId ? Number(filters.sectorId) : null,
    job_position_id: filters.jobPositionId
  }

  const loadBoard = () => {
    const version = ++boardVersion.current
    setRefreshing(true)
    setLoadError(false)
    return Promise.all(
      ADMISSION_STAGES.map(({ stage }) =>
        service.admissions(stage, apiFilters, 0, PAGE_SIZE).then((page) => [stage, page] as const)
      )
    )
      .then((pages) => {
        if (version !== boardVersion.current) return
        const next = emptyBoard()
        pages.forEach(([stage, page]) => {
          next[stage] = { items: page.data, total: page.total, loadingMore: false }
        })
        setBoard(next)
      })
      .catch((error) => {
        if (version !== boardVersion.current) return
        setBoard(emptyBoard())
        setLoadError(true)
        toast.error('Erro ao carregar as admissões', error)
      })
      .finally(() => {
        if (version !== boardVersion.current) return
        setLoading(false)
        setRefreshing(false)
      })
  }

  useEffect(() => {
    loadBoard()
  }, [filters])

  // Deep link: /admissoes?card={id}
  useEffect(() => {
    const id = Number(searchParams.get('card'))
    if (id) setOpenCardId(id)
  }, [searchParams])

  const loadMore = (stage: TAdmissionStage) => {
    const column = board[stage]
    if (column.loadingMore) return

    const version = boardVersion.current
    setBoard((current) => ({ ...current, [stage]: { ...current[stage], loadingMore: true } }))
    service
      .admissions(stage, apiFilters, column.items.length, PAGE_SIZE)
      .then((page) => {
        if (version !== boardVersion.current) return
        setBoard((current) => {
          const known = new Set(current[stage].items.map((item) => item.id))
          return {
            ...current,
            [stage]: {
              items: [...current[stage].items, ...page.data.filter((item) => !known.has(item.id))],
              total: page.total,
              loadingMore: false
            }
          }
        })
      })
      .catch((error) => {
        if (version !== boardVersion.current) return
        toast.error('Erro ao carregar mais admissões', error)
        setBoard((current) => ({ ...current, [stage]: { ...current[stage], loadingMore: false } }))
      })
  }

  const updateFilters = (patch: Partial<IFilters>) => setFilters((current) => ({ ...current, ...patch }))

  const closeCard = () => {
    setOpenCardId(null)
    if (searchParams.has('card')) setSearchParams({}, { replace: true })
  }

  return (
    <>
      <PageTitle title="Admissão de Colaborador" description="Admissões enviadas pelo formulário público" />

      <FilterBar
        onClear={() => updateFilters(INITIAL_FILTERS)}
        isDirty={JSON.stringify(filters) !== JSON.stringify(INITIAL_FILTERS)}
      >
        <FilterSearch
          value={filters.search}
          onChange={(search) => updateFilters({ search })}
          placeholder="nome / usuário"
        />
        <FilterSelect
          label="Setor"
          value={filters.sectorId}
          onChange={(sectorId) => updateFilters({ sectorId })}
          options={sectors}
        />
        <FilterField label="Cargo">
          <SearchableSelect<number>
            size="sm"
            isClearable
            options={roleOptions}
            value={filters.jobPositionId ?? ''}
            onChange={(jobPositionId) => updateFilters({ jobPositionId: jobPositionId === '' ? null : jobPositionId })}
            placeholder="Todos"
            entity="cargo"
          />
        </FilterField>
        {refreshing && !loading && <Spinner size="sm" color="gray.500" alignSelf="center" />}
      </FilterBar>

      {loading ? (
        <LoadingBlock size="page" entity="admissões" />
      ) : (
        <Flex gap={4} overflowX="auto" pb={2} align="flex-start">
          {ADMISSION_STAGES.map(({ stage, label }) => {
            const column = board[stage]
            const remaining = column.total - column.items.length

            return (
              <Flex
                key={stage}
                direction="column"
                flex="0 1 360px"
                minW="300px"
                bg="gray.50"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="lg"
                p={3}
              >
                <Flex align="center" justify="space-between" px={1} py={1} mb={2}>
                  <Text fontWeight="bold" fontSize="sm">
                    {label}
                  </Text>
                  <Badge>{column.total}</Badge>
                </Flex>
                <Flex direction="column" gap={2} overflowY="auto" maxH="calc(100vh - 260px)" minH="60px">
                  {loadError ? (
                    <EmptyState
                      tone="error"
                      icon="lni lni-warning"
                      title="Não foi possível carregar as admissões"
                      action={<ActionButton action="refresh" label="Tentar novamente" showLabel onClick={loadBoard} />}
                    />
                  ) : (
                    column.items.length === 0 && <EmptyState icon="lni lni-users" title="Nenhuma admissão encontrada" />
                  )}
                  {column.items.map((card) => (
                    <AdmissionCard key={card.id} card={card} onOpen={setOpenCardId} />
                  ))}
                  {remaining > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      colorScheme="magenta"
                      onClick={() => loadMore(stage)}
                      isLoading={column.loadingMore}
                    >
                      Carregar mais ({remaining})
                    </Button>
                  )}
                </Flex>
              </Flex>
            )
          })}
        </Flex>
      )}

      <EmployeeViewModal
        isOpen={openCardId !== null}
        onClose={closeCard}
        employeeId={openCardId}
        roles={roles}
        sectors={sectors}
        onChanged={loadBoard}
      />

      <ActionFab label="Abrir o formulário de admissão" to="/admissao" openInNewTab />
    </>
  )
}
